import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";
import AffiliateGearCard from "@/components/AffiliateGearCard";
import NewsletterSection from "@/components/NewsletterSection";
import { Reveal, Eyebrow } from "@/components/Motion";
import { GEAR, GEAR_CATEGORIES } from "@/data/gear";
import { Info } from "lucide-react";

export default function Gear() {
    const [cat, setCat] = useState("All");
    const filtered = cat === "All" ? GEAR : GEAR.filter((g) => g.category === cat);

    return (
        <>
            <SEO
                title="Trout Gear & Tackle — Field-Tested Picks"
                description="Curated fly fishing gear: rods, reels, flies, waders, and accessories — every pick fished hard and honestly reviewed."
            />
            <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-44 pb-16" data-testid="gear-hero">
                <Reveal>
                    <Eyebrow>Gear & Affiliates</Eyebrow>
                    <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 leading-[1.05]">
                        The catalog of <span className="text-coral">proven things.</span>
                    </h1>
                    <p className="mt-6 font-body text-lg text-slate-400 max-w-2xl">
                        Every rod, fly, and wader below has been fished hard by us. Buy through our links and we earn a small commission — it funds the next trip and keeps every guide free.
                    </p>
                </Reveal>

                <Reveal delay={0.12}>
                    <div className="mt-8 flex items-start gap-3 rounded-2xl border border-gold/25 bg-gold/5 p-5 max-w-3xl" data-testid="affiliate-disclosure">
                        <Info size={18} className="mt-0.5 shrink-0 text-gold" />
                        <p className="font-body text-sm text-slate-300">
                            <span className="font-semibold text-gold">Affiliate disclosure:</span> links below go to our partners (Amazon, Cabela's and others). Prices shown are indicative — check the partner site for today's deal. We never recommend gear we haven't fished. As an Amazon Associate we earn from qualifying purchases.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-10 flex flex-wrap gap-2" data-testid="gear-filters">
                        {GEAR_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCat(c)}
                                data-testid={`gear-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className={`relative rounded-full px-5 py-2.5 font-sans text-sm font-medium transition-colors duration-300 ${
                                    cat === c ? "text-[#0B1320]" : "text-slate-300 hover:text-teal border border-white/15 hover:border-teal/50"
                                }`}
                            >
                                {cat === c && (
                                    <motion.span
                                        layoutId="gear-filter-pill"
                                        className="absolute inset-0 rounded-full bg-teal"
                                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                                    />
                                )}
                                <span className="relative">{c}</span>
                            </button>
                        ))}
                    </div>
                </Reveal>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32" data-testid="gear-grid-section">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={cat}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.35 }}
                        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {filtered.map((item, i) => (
                            <AffiliateGearCard key={item.id} item={item} index={i} />
                        ))}
                    </motion.div>
                </AnimatePresence>
            </section>

            <NewsletterSection />
        </>
    );
}
